import React, { useState, useEffect } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Shield, RotateCcw, Save, Loader2, Eye } from 'lucide-react';
import { DEFAULT_PAGE_PERMISSIONS, savePagePermissions, resetPagePermissions, updatePermissionsCache } from '@/utils/permissions-config';

const PermissionEditor = () => {
  const [permissions, setPermissions] = useState(DEFAULT_PAGE_PERMISSIONS);
  const [storeVisibility, setStoreVisibility] = useState({ allowed_roles: ['network_admin', 'regional_manager'] });
  const [hasChanges, setHasChanges] = useState(false);
  const [hasVisibilityChanges, setHasVisibilityChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    // Load permissions from database on mount
    const loadPermissions = async () => {
      try {
        const [permResponse, visibilityResponse] = await Promise.all([
          fetch(`${API}/page-permissions`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API}/store-visibility-settings`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        if (permResponse.ok) {
          const data = await permResponse.json();
          if (data && typeof data === 'object' && Object.keys(data).length > 0) {
            setPermissions(data);
            updatePermissionsCache(data);
          }
        }
        
        if (visibilityResponse.ok) {
          const visData = await visibilityResponse.json();
          setStoreVisibility(visData);
        }
      } catch (e) {
        console.error('Failed to load permissions:', e);
      } finally {
        setLoading(false);
      }
    };
    
    loadPermissions();
  }, [token]);

  const roles = [
    { key: 'none', name: 'Basic Access', color: 'bg-slate-100 text-slate-700' },
    { key: 'stocktake_champion', name: 'Champion', color: 'bg-blue-100 text-blue-700' },
    { key: 'stocktake_controller', name: 'Controller', color: 'bg-purple-100 text-purple-700' },
    { key: 'stocktake_auditor', name: 'Auditor', color: 'bg-orange-100 text-orange-700' },
    { key: 'supervisor', name: 'Supervisor', color: 'bg-amber-100 text-amber-700' },
    { key: 'store_manager', name: 'Coordinator', color: 'bg-green-100 text-green-700' },
    { key: 'complex_manager', name: 'Complex Manager', color: 'bg-teal-100 text-teal-700' },
    { key: 'regional_manager', name: 'Regional Manager', color: 'bg-indigo-100 text-indigo-700' },
    { key: 'network_admin', name: 'Network Admin', color: 'bg-red-100 text-red-700' }
  ];

  const pages = [
    { path: '/', name: 'Dashboard' },
    { path: '/readiness', name: 'Readiness' },
    { path: '/scheduling', name: 'Scheduling' },
    { path: '/pre-stocktake', name: 'Pre-Stocktake' },
    { path: '/roster-areas', name: 'Roster & Areas' },
    { path: '/device-pool', name: 'Device Pool' },
    { path: '/store-map', name: 'Store Map' },
    { path: '/checklist', name: 'Checklist' },
    { path: '/attendance', name: 'Attendance' },
    { path: '/miscounts', name: 'Miscounts' },
    { path: '/verifications', name: 'Verifications' },
    { path: '/admin-cutoff', name: 'Admin Cut-off' },
    { path: '/top-stock', name: 'Top Stock' },
    { path: '/settings', name: 'Settings' }
  ];

  const togglePermission = (pagePath, roleKey) => {
    setPermissions(prev => {
      const pagePerms = prev[pagePath] || [];
      const newPerms = { ...prev };
      
      if (pagePerms.includes(roleKey)) {
        // Remove permission
        newPerms[pagePath] = pagePerms.filter(r => r !== roleKey);
      } else {
        // Add permission
        newPerms[pagePath] = [...pagePerms, roleKey];
      }
      
      setHasChanges(true);
      return newPerms;
    });
  };

  const hasPermission = (pagePath, roleKey) => {
    return permissions[pagePath]?.includes(roleKey) || false;
  };

  const hasStoreVisibility = (roleKey) => {
    return storeVisibility.allowed_roles?.includes(roleKey) || false;
  };

  const toggleStoreVisibility = (roleKey) => {
    // Cannot toggle network_admin or regional_manager - they always have access
    if (roleKey === 'network_admin' || roleKey === 'regional_manager') return;
    
    setStoreVisibility(prev => {
      const currentRoles = prev.allowed_roles || [];
      let newRoles;
      
      if (currentRoles.includes(roleKey)) {
        newRoles = currentRoles.filter(r => r !== roleKey);
      } else {
        newRoles = [...currentRoles, roleKey];
      }
      
      setHasVisibilityChanges(true);
      return { ...prev, allowed_roles: newRoles };
    });
  };

  const handleSaveVisibility = async () => {
    setSavingVisibility(true);
    try {
      const response = await fetch(`${API}/store-visibility-settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ allowed_roles: storeVisibility.allowed_roles })
      });
      
      if (response.ok) {
        const data = await response.json();
        setStoreVisibility(data);
        setHasVisibilityChanges(false);
        toast.success('Store visibility settings saved');
      } else {
        toast.error('Failed to save store visibility settings');
      }
    } catch (e) {
      toast.error('Failed to save store visibility settings');
    } finally {
      setSavingVisibility(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const success = await savePagePermissions(permissions, token);
    setSaving(false);
    
    if (success) {
      toast.success('Permissions saved to database');
      setHasChanges(false);
      // Update cache and recommend reload
      updatePermissionsCache(permissions);
      toast.info('Please refresh the page for changes to take effect', { duration: 5000 });
    } else {
      toast.error('Failed to save permissions');
    }
  };

  const handleReset = async () => {
    if (window.confirm('Reset all permissions to default? This cannot be undone.')) {
      setSaving(true);
      const success = await resetPagePermissions(token);
      setSaving(false);
      
      if (success) {
        setPermissions(DEFAULT_PAGE_PERMISSIONS);
        setHasChanges(false);
        updatePermissionsCache(null);
        toast.success('Permissions reset to defaults');
        toast.info('Please refresh the page for changes to take effect', { duration: 5000 });
      } else {
        toast.error('Failed to reset permissions');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
        <span className="ml-2 text-slate-600">Loading permissions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Customize Role Permissions</h3>
            <p className="text-sm text-blue-700">
              Toggle which pages each role can access. Changes are saved to the database and apply to all users.
              <strong className="block mt-1">Network Admin always has full access.</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="save-permissions"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
          <Button
            onClick={handleReset}
            variant="outline"
            disabled={saving}
            data-testid="reset-permissions"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Default
          </Button>
        </div>
        {hasChanges && (
          <span className="text-sm text-amber-600 font-medium">
            You have unsaved changes
          </span>
        )}
      </div>

      {/* Permissions Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b">
              <th className="text-left p-3 font-semibold text-slate-700 sticky left-0 bg-slate-50 min-w-[140px]">
                Page
              </th>
              {roles.map(role => (
                <th key={role.key} className="text-center p-2 font-medium min-w-[90px]">
                  <span className={`inline-block px-2 py-1 rounded text-xs ${role.color}`}>
                    {role.name}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pages.map(page => (
              <tr key={page.path} className="border-b hover:bg-slate-50">
                <td className="p-3 font-medium text-slate-700 sticky left-0 bg-white">
                  {page.name}
                </td>
                {roles.map(role => {
                  const isNetworkAdmin = role.key === 'network_admin';
                  const isEnabled = hasPermission(page.path, role.key);
                  
                  return (
                    <td key={role.key} className="text-center p-2">
                      <div className="flex justify-center">
                        <Switch
                          checked={isNetworkAdmin || isEnabled}
                          onCheckedChange={() => !isNetworkAdmin && togglePermission(page.path, role.key)}
                          disabled={isNetworkAdmin}
                          className={`${!isEnabled && !isNetworkAdmin ? 'opacity-40' : ''} ${isNetworkAdmin ? 'cursor-not-allowed' : ''}`}
                          data-testid={`perm-${page.path.replace('/', '') || 'dashboard'}-${role.key}`}
                        />
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Store Data Visibility Section */}
      <div className="mt-8 pt-6 border-t border-slate-200">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <Eye className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Cross-Store Data Visibility</h3>
              <p className="text-sm text-amber-700">
                Control which roles can view data from <strong>all stores</strong> in the network.
                Roles without this permission can only see their own store's data.
                <strong className="block mt-1">Network Admin and Regional Manager always have cross-store access.</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-slate-800">Enable Cross-Store View For:</h4>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSaveVisibility}
              disabled={!hasVisibilityChanges || savingVisibility}
              size="sm"
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="save-visibility"
            >
              {savingVisibility ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Visibility Settings
            </Button>
            {hasVisibilityChanges && (
              <span className="text-sm text-amber-600 font-medium">Unsaved</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {roles.map(role => {
            const isLocked = role.key === 'network_admin' || role.key === 'regional_manager';
            const isEnabled = hasStoreVisibility(role.key);
            
            return (
              <div 
                key={role.key}
                className={`flex items-center justify-between p-3 rounded-lg border ${isEnabled ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}
              >
                <span className={`text-sm font-medium ${role.color} px-2 py-0.5 rounded`}>
                  {role.name}
                </span>
                <Switch
                  checked={isEnabled}
                  onCheckedChange={() => toggleStoreVisibility(role.key)}
                  disabled={isLocked}
                  className={isLocked ? 'cursor-not-allowed' : ''}
                  data-testid={`visibility-${role.key}`}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PermissionEditor;
